import html2canvas from "html2canvas";

const LOGO_PATH = `${process.env.PUBLIC_URL || ""}/logo.png`;

let logoPreloadPromise: Promise<void> | null = null;

export const preloadCertificateAssets = (): Promise<void> => {
  if (logoPreloadPromise) return logoPreloadPromise;

  logoPreloadPromise = new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve();
    img.onerror = () => resolve();
    img.src = LOGO_PATH;
  });

  return logoPreloadPromise;
};

const waitForPaint = () =>
  new Promise<void>((resolve) => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => resolve());
    });
  });

const getCertificateElement = (node: HTMLElement): HTMLElement => {
  const certificate = node.querySelector(".certificate") as HTMLElement | null;
  return certificate || node;
};

export const captureCertificateElement = async (
  node: HTMLElement,
): Promise<HTMLCanvasElement> => {
  await preloadCertificateAssets();

  const target = getCertificateElement(node);
  target.scrollIntoView({ block: "center", inline: "nearest" });
  await waitForPaint();
  await new Promise((resolve) => setTimeout(resolve, 150));

  const width = target.offsetWidth;
  const height = target.offsetHeight;
  if (!width || !height) {
    throw new Error(
      `Certificate has invalid dimensions (${width}x${height})`,
    );
  }

  return html2canvas(target, {
    backgroundColor: "#0a1830",
    scale: 3,
    useCORS: true,
    allowTaint: true,
    logging: false,
    imageTimeout: 20000,
    scrollX: 0,
    scrollY: 0,
    onclone: (clonedDoc, clonedElement) => {
      const root = clonedElement as HTMLElement;
      const certColor =
        getComputedStyle(root).getPropertyValue("--cert-color").trim() ||
        "#3FA9FF";

      root.querySelectorAll("img").forEach((img) => {
        const image = img as HTMLImageElement;
        if (!image.complete || image.naturalWidth === 0) {
          image.style.visibility = "hidden";
        } else {
          image.crossOrigin = "anonymous";
        }
      });

      root
        .querySelectorAll(
          ".certificate__swoosh, .certificate__laurel, .certificate__corner",
        )
        .forEach((el) => {
          (el as HTMLElement).style.display = "none";
        });

      root.querySelectorAll(".certificate").forEach((el) => {
        const certificate = el as HTMLElement;
        certificate.style.boxShadow = "none";
        certificate.style.background =
          "linear-gradient(135deg, #0a1830 0%, #050d1f 60%, #020613 100%)";
        certificate.style.border = `2px solid ${certColor}`;
        certificate.style.borderRadius = "14px";
      });

      root.querySelectorAll(".certificate__inner").forEach((el) => {
        const inner = el as HTMLElement;
        inner.style.border = `2px solid ${certColor}`;
        inner.style.borderRadius = "8px";
        inner.style.boxShadow = "none";
        inner.style.background = "transparent";
      });

      const style = clonedDoc.createElement("style");
      style.textContent = `
        .certificate, .certificate__inner {
          overflow: hidden !important;
        }
        .certificate__corner {
          display: none !important;
        }
      `;
      clonedDoc.head.appendChild(style);
    },
  });
};
